<script setup lang="ts">
/**
 * One item of a team's work list (#1830): what a board card, an execution card and a Waiting row open. Its fields,
 * its body and links, the run holding it, and its whole history, oldest first, with who made each change. A person
 * shapes the list from here too (#1831): approve, decline, move, hold, release and drop, each kept in the history.
 * A parent (an epic, a campaign) lists every one of its children with where it stands, closed ones included.
 */
import { ArrowLeft, RefreshCw } from '@lucide/vue'
import {
    Button,
    ZhEmptyState,
    ZhKeyValue,
    ZhPageTitle,
    ZhPanel,
    ZhSectionHeading,
    ZhStatusDot,
    ZhStatusPill,
} from '@zerohuman/ui'
import { computed } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { api, type WorkItemHistoryEntry, type WorkItemRow } from '@/api'
import ExecutionStatus from '@/components/ExecutionStatus.vue'
import WorkItemActions from '@/components/WorkItemActions.vue'
import { useLiveResource } from '@/composables/useLiveResource'
import { PRIORITY_LABELS } from '@/lib/boardLanes'
import { statusLabel, statusTone } from '@/lib/statusTone'

const route = useRoute()
const id = computed(() => String(route.params.id))
const {
    data: item,
    error,
    loading,
    refreshing,
    visible,
    refresh,
} = useLiveResource(
    () => id.value,
    signal => api.workItem(id.value, signal),
)
const { data: history, refresh: rereadHistory } = useLiveResource(
    () => `${id.value}:history`,
    signal => api.workItemHistory(id.value, signal),
)

/**
 * A parent's children, read only while the item has any (an item with none asks for nothing), on the item's own clock.
 * The key turns when the item gains or loses its children, so the list reads at once.
 */
const hasChildren = computed(() => (item.value?.children?.total ?? 0) > 0)
const { data: children, refresh: rereadChildren } = useLiveResource(
    () => `${id.value}:children:${hasChildren.value}`,
    signal => (hasChildren.value ? api.workItemChildren(id.value, signal) : Promise.resolve([])),
    { active: () => hasChildren.value },
)
/** How many children are in each state, in the order an item moves, from the item's own rollup. */
const CHILD_STATES: readonly WorkItemRow['state'][] = ['proposed', 'ready', 'held', 'in_progress', 'done', 'dropped']
const childRollup = computed(() =>
    CHILD_STATES.flatMap(state => {
        const count = item.value?.children?.byState[state] ?? 0
        return count > 0 ? [[state, count] as const] : []
    }),
)
/** The status of the run holding a child, or null when none holds it. */
function childRunStatus(child: WorkItemRow): string | null {
    return !child.closedAt && child.claimedByRunId ? (child.claimedByRunStatus ?? null) : null
}
/** A link in a few words: its path, without the host, or the whole link when it is not an address. */
function linkLabel(link: string): string {
    try {
        const url = new URL(link)
        return url.pathname.replace(/^\/+/, '') || url.host
    } catch {
        return link
    }
}

/** Who or what acted, as the item's `addedBy` and each history entry's `actor` say it. */
function actorLabel(actor: Record<string, unknown> | null | undefined): string {
    switch (actor?.['kind']) {
        case 'run':
            return `Run ${String(actor['runId'] ?? '').slice(0, 8)}`
        case 'member':
            return 'A team member'
        case 'api_key':
            return 'An API key'
        case 'person':
            return 'A person'
        default:
            return 'Unknown'
    }
}

/** A changed value in words: text as it is, anything else as JSON, nothing as an em dash. */
function value(given: unknown): string {
    if (given === null || given === undefined || given === '') return '—'
    return typeof given === 'string' ? given : JSON.stringify(given)
}

function changeLines(entry: WorkItemHistoryEntry): string[] {
    return Object.entries(entry.changes ?? {}).map(
        ([field, change]) => `${field}: ${value(change.before)} → ${value(change.after)}`,
    )
}

function date(at: string): string {
    return new Date(at).toLocaleString()
}

async function refreshAll(): Promise<void> {
    await Promise.all([refresh(), rereadHistory(), ...(hasChildren.value ? [rereadChildren()] : [])])
}
</script>

<template>
    <section class="w-full min-w-0 space-y-5 p-4 md:p-8">
        <RouterLink to="/executions" class="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-sm"><ArrowLeft :size="15" /> Executions</RouterLink>
        <p v-if="loading && !item" role="status" class="text-muted-foreground py-16 text-center text-sm">Loading work item…</p>
        <ZhPanel v-if="error" as="div" tone="failed" role="alert" class="flex flex-wrap items-center justify-between gap-3 text-sm">
            <span>{{ item ? 'Live updates interrupted. The last received state is shown below.' : 'Could not load this work item. It may no longer be available.' }}</span>
            <Button variant="outline" size="sm" :disabled="refreshing" @click="refreshAll">Try again</Button>
        </ZhPanel>
        <template v-if="item">
            <header class="flex flex-wrap items-start justify-between gap-4">
                <div class="min-w-0 space-y-2">
                    <div class="flex flex-wrap items-center gap-3"><ZhPageTitle kicker="Work item">{{ item.title }}</ZhPageTitle><ExecutionStatus :status="item.state" /></div>
                    <p class="text-muted-foreground text-xs">{{ item.kind }}<span v-if="item.priority"> · {{ PRIORITY_LABELS[item.priority] }}</span> · Added {{ date(item.createdAt) }} by {{ actorLabel(item.addedBy) }}</p>
                    <!-- #431: an item another team's run sent says where it came from: that team, and the run that sent it. -->
                    <p v-if="item.origin" class="text-muted-foreground text-xs" data-testid="work-item-origin">from {{ item.origin.teamName }} · <RouterLink :to="`/runs/${item.origin.runId}`" class="text-primary underline underline-offset-2">run {{ item.origin.runId.slice(0, 8) }}</RouterLink></p>
                    <p class="text-muted-foreground break-all font-mono text-xs">{{ item.id }}</p>
                    <WorkItemActions v-if="!item.closedAt" :item="{ id: item.id, title: item.title, state: item.state, claimed: Boolean(item.claimedByRunId) && item.state === 'in_progress' }" @changed="refreshAll" />
                </div>
                <div class="flex items-center gap-3">
                    <span class="text-muted-foreground flex items-center gap-1.5 text-xs"><ZhStatusDot :state="error ? 'active' : visible ? 'complete' : 'inactive'" :pulse="visible" />{{ error ? 'Reconnecting…' : visible ? 'Live' : 'Updates paused' }}</span>
                    <Button variant="outline" size="icon-sm" aria-label="Refresh work item" :disabled="refreshing" @click="refreshAll"><RefreshCw :size="15" /></Button>
                </div>
            </header>

            <div class="flex flex-col gap-4 lg:flex-row lg:items-start">
                <ZhPanel as="article" class="min-w-0 space-y-4 lg:flex-1">
                    <ZhSectionHeading>The work</ZhSectionHeading>
                    <p v-if="item.body" class="whitespace-pre-wrap text-sm" data-testid="work-item-body">{{ item.body }}</p>
                    <p v-else class="text-muted-foreground text-xs">No body.</p>
                    <ul v-if="item.links.length" class="space-y-1 text-xs" data-testid="work-item-links">
                        <li v-for="link in item.links" :key="link"><a :href="link" target="_blank" rel="noopener noreferrer" class="text-primary break-all underline underline-offset-2">{{ link }}</a></li>
                    </ul>
                </ZhPanel>
                <ZhPanel as="aside" class="min-w-0 space-y-3 lg:w-80">
                    <ZhSectionHeading>Where it stands</ZhSectionHeading>
                    <dl>
                        <ZhKeyValue label="Team">{{ item.teamId }}</ZhKeyValue>
                        <ZhKeyValue label="Position">{{ item.position }}</ZhKeyValue>
                        <ZhKeyValue label="Held by">
                            <RouterLink v-if="item.claimedByRunId" :to="`/runs/${item.claimedByRunId}`" class="text-primary underline underline-offset-2" data-testid="work-item-holder">run {{ item.claimedByRunId.slice(0, 8) }}</RouterLink>
                            <span v-else>No run</span>
                        </ZhKeyValue>
                        <ZhKeyValue v-if="item.claimedAt" label="Since">{{ date(item.claimedAt) }}</ZhKeyValue>
                        <!-- #575: a held item that still names a run is one whose pipeline stopped short. -->
                        <ZhKeyValue v-if="item.state === 'held' && item.claimedByRunId" label="Why held"><span data-testid="work-item-stopped">Its pipeline stopped. Retry or clear the run on Blockers to go on.</span></ZhKeyValue>
                        <ZhKeyValue v-if="item.closedAt" label="Closed">{{ date(item.closedAt) }}</ZhKeyValue>
                        <ZhKeyValue v-if="item.closeReason" label="Reason">{{ item.closeReason }}</ZhKeyValue>
                    </dl>
                </ZhPanel>
            </div>

            <ZhPanel v-if="hasChildren" as="section" class="space-y-3" data-testid="work-item-children">
                <div class="flex flex-wrap items-center justify-between gap-2">
                    <ZhSectionHeading>Children ({{ item.children?.total ?? 0 }})</ZhSectionHeading>
                    <span class="flex flex-wrap items-center gap-1.5">
                        <span v-for="[state, count] in childRollup" :key="state" :data-rollup="state">
                            <ZhStatusPill :tone="statusTone(state)" :pulse="false">{{ count }} {{ statusLabel(state) }}</ZhStatusPill>
                        </span>
                    </span>
                </div>
                <p v-if="!children" role="status" class="text-muted-foreground text-xs">Loading its children…</p>
                <ul v-else class="divide-border divide-y text-sm">
                    <li v-for="child in children" :key="child.id" :data-child="child.id" :data-item-state="child.state" class="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
                        <RouterLink :to="`/work-items/${child.id}`" class="hover:text-primary min-w-0 flex-1 font-medium">{{ child.title }}</RouterLink>
                        <span v-if="child.priority" class="text-muted-foreground text-xs">{{ PRIORITY_LABELS[child.priority] }}</span>
                        <a v-if="child.links[0]" :href="child.links[0]" target="_blank" rel="noopener noreferrer" class="text-muted-foreground hover:text-primary font-mono text-xs underline underline-offset-2">{{ linkLabel(child.links[0]) }}</a>
                        <ExecutionStatus :status="child.state" />
                        <RouterLink v-if="childRunStatus(child)" :to="`/runs/${child.claimedByRunId}`" class="text-muted-foreground flex items-center gap-1 text-xs" data-testid="child-run">Run <ExecutionStatus :status="childRunStatus(child) ?? ''" /></RouterLink>
                    </li>
                </ul>
            </ZhPanel>

            <ZhPanel as="section" class="space-y-3">
                <ZhSectionHeading>History</ZhSectionHeading>
                <ZhEmptyState v-if="!history || history.length === 0" compact>No changes recorded yet.</ZhEmptyState>
                <ol v-else class="space-y-3 text-xs" data-testid="work-item-history">
                    <li v-for="entry in history" :key="entry.id" class="space-y-1">
                        <p class="font-medium">{{ actorLabel(entry.actor) }} <span class="text-muted-foreground font-normal">· {{ date(entry.at) }}</span></p>
                        <ul class="text-muted-foreground space-y-0.5">
                            <!-- #431: the first entry of an item another team's run sent says where it came from. -->
                            <li v-if="entry.origin" class="break-words" data-testid="work-item-history-origin">Sent from {{ entry.origin.teamName }}</li>
                            <li v-for="line in changeLines(entry)" :key="line" class="break-words">{{ line }}</li>
                        </ul>
                    </li>
                </ol>
            </ZhPanel>
        </template>
    </section>
</template>
